import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useCallback, useRef } from 'react';

import { useOverflowing } from '@/components/home/useOverflowing';
import { TESTIMONIALS, TESTIMONIALS_SECTION, type Testimonial } from '@/data/homeContent';
import { cn } from '@/lib/cn';

/**
 * What merchants say - the left half of the second two-column row.
 *
 * A scroll-snapping rail rather than a slideshow: on a phone one card fills
 * the width and a thumb moves it, and from `lg` exactly three sit side by side,
 * which is the design's arrangement. Three cards then fit the rail precisely,
 * so there is nothing to scroll and the arrows stay away; add a fourth
 * testimonial and the rail overflows and they come back on their own.
 *
 * The rail keeps native scrolling, so it is operable from the keyboard without
 * the arrows. They are a shortcut, not the only way through.
 */

/** Initials stand in until there are merchant photographs to use. */
function initialsOf(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function Stars({ rating }: { rating: number }) {
  return (
    <p className="flex gap-0.5" aria-label={`Rated ${rating} out of 5`}>
      {Array.from({ length: 5 }, (_, index) => (
        <Star
          key={index}
          className={cn(
            'size-3',
            index < rating ? 'fill-saffron-500 text-saffron-500' : 'text-line',
          )}
          aria-hidden
        />
      ))}
    </p>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <figure className="flex h-full flex-col rounded-xl border border-line bg-white p-3 sm:p-3.5">
      {/* Portrait and quote share the top row; the attribution below starts
          back at the card's edge, under the portrait rather than beside it.
          The portrait is small on purpose - three cards across half a 1152px
          row leaves about 145px of card, and every pixel the circle takes is
          one the quote beside it loses. At 28px the quote keeps roughly the
          two-thirds of the card the design gives it. */}
      <div className="flex gap-2.5">
        <span
          className="grid size-7 shrink-0 place-items-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700"
          aria-hidden
        >
          {initialsOf(testimonial.name)}
        </span>
        <blockquote className="min-w-0 flex-1 text-[11px] leading-relaxed text-ink-soft">
          &ldquo;{testimonial.quote}&rdquo;
        </blockquote>
      </div>

      <figcaption className="mt-3">
        <span className="block text-xs font-bold leading-snug tracking-tight text-ink">
          {testimonial.name}
        </span>
        <span className="mt-0.5 block text-[11px] leading-snug text-muted">
          {testimonial.business}
        </span>
      </figcaption>

      <div className="mt-2">
        <Stars rating={testimonial.rating} />
      </div>
    </figure>
  );
}

export function TestimonialsCarousel() {
  const rail = useRef<HTMLUListElement>(null);
  const overflowing = useOverflowing(rail);

  /** Move by exactly one card, gap included, in the reading direction. */
  const scrollByCard = useCallback((direction: 1 | -1) => {
    const node = rail.current;
    const card = node?.firstElementChild;
    if (!node || !card) return;

    const step = card.getBoundingClientRect().width + 12;
    // A right-to-left rail scrolls into negative offsets, so the direction has
    // to follow the document rather than the arrow that was pressed.
    const rtl = getComputedStyle(node).direction === 'rtl';
    node.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: 'smooth' });
  }, []);

  return (
    <section
      aria-labelledby="home-testimonials"
      className="rounded-2xl border border-line/70 bg-white p-4 shadow-card sm:p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <h2
          id="home-testimonials"
          className="text-lg font-bold tracking-tight text-ink sm:text-xl"
        >
          {TESTIMONIALS_SECTION.heading}
        </h2>

        {overflowing ? (
          <div className="-me-2.5 -mt-1.5 flex shrink-0">
            <RailButton
              label={TESTIMONIALS_SECTION.previousLabel}
              onClick={() => scrollByCard(-1)}
              side="start"
            />
            <RailButton
              label={TESTIMONIALS_SECTION.nextLabel}
              onClick={() => scrollByCard(1)}
              side="end"
            />
          </div>
        ) : null}
      </div>

      <ul
        ref={rail}
        tabIndex={overflowing ? 0 : undefined}
        aria-label={TESTIMONIALS_SECTION.railLabel}
        className="no-scrollbar mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1"
      >
        {TESTIMONIALS.map((testimonial) => (
          <li
            key={testimonial.name}
            // Exactly three across from `lg`, gaps accounted for, so three
            // testimonials fit the rail to the pixel and a fourth overflows.
            className="w-[85%] shrink-0 snap-start sm:w-[47%] lg:w-[calc((100%-1.5rem)/3)]"
          >
            <TestimonialCard testimonial={testimonial} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function RailButton({
  label,
  onClick,
  side,
}: {
  label: string;
  onClick: () => void;
  side: 'start' | 'end';
}) {
  const Icon = side === 'start' ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      // The circle the design draws is 28px, which is too small to aim a thumb
      // at. The button keeps its 44px of hit area and only the ring inside it
      // is drawn, so it looks like the design and still takes a tap.
      className="group/rail grid size-11 place-items-center"
    >
      <span className="grid size-7 place-items-center rounded-full border border-line bg-white text-muted transition-colors group-hover/rail:border-brand-300 group-hover/rail:text-brand-600">
        <Icon className="size-3.5 rtl-flip" aria-hidden />
      </span>
    </button>
  );
}
